import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";

const TestimonialsSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Budi Santoso",
      position: "General Manager",
      company: "SKK Migas",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      rating: 5,
      content:
        "RanselPro memberikan hasil video company profile yang sangat profesional dan berkualitas tinggi. Tim mereka sangat memahami kebutuhan korporat dan mampu menerjemahkannya dalam visual yang menarik.",
      project: "Company Profile Video Production",
    },
    {
      id: 2,
      name: "Sari Dewi",
      position: "Brand Manager",
      company: "Pertamina",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b633?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      rating: 5,
      content:
        "Kerjasama dengan RanselPro untuk pengembangan brand identity kami berjalan sangat lancar. Mereka memiliki pemahaman yang mendalam tentang strategi branding dan mampu menghadirkan solusi kreatif yang tepat sasaran.",
      project: "Brand Identity Development",
    },
    {
      id: 3,
      name: "Dr. Ahmad Rahman",
      position: "Director of Communications",
      company: "UNESCO-IHE Delft",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      rating: 5,
      content:
        "The 3D animation project for our educational program exceeded our expectations. RanselPro's team demonstrated exceptional technical skills and creativity in delivering content that effectively communicates complex concepts.",
      project: "3D Educational Animation",
    },
    {
      id: 4,
      name: "Maya Kusuma",
      position: "IT Director",
      company: "Inovasia",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      rating: 5,
      content:
        "Platform e-commerce yang dikembangkan RanselPro sangat robust dan user-friendly. Mereka tidak hanya fokus pada teknis, tetapi juga memahami aspek bisnis dan user experience dengan baik.",
      project: "E-Commerce Platform Development",
    },
    {
      id: 5,
      name: "Rizki Pratama",
      position: "Marketing Head",
      company: "Bank Mandiri",
      avatar:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      rating: 5,
      content:
        "Kampanye digital yang dirancang RanselPro berhasil meningkatkan engagement dan brand awareness kami secara signifikan. Tim mereka sangat responsif dan selalu memberikan solusi inovatif untuk setiap challenge.",
      project: "Digital Marketing Campaign",
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating ? "text-yellow-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  return (
    <section
      id="testimonials"
      className="py-20 bg-gradient-to-br from-blue-50 to-orange-50"
    >
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Testimoni
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Apa Kata{" "}
              <span className="text-gradient-blue-orange">Klien Kami</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Dengarkan pengalaman langsung dari klien-klien yang telah
              mempercayakan project mereka kepada RanselPro.
            </p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative">
            <Card className="border-0 bg-white shadow-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="relative flex items-center justify-center py-16 md:py-20 min-h-[26rem] transition-all duration-500 ease-in-out">
                  {/* Navigation Buttons */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={prevSlide}
                    className="absolute left-4 z-10 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm border-white/50 hover:bg-white shadow-lg"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                  </Button>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={nextSlide}
                    className="absolute right-4 z-10 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm border-white/50 hover:bg-white shadow-lg"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-700" />
                  </Button>

                  {/* Testimonial Content */}
                  <div className="w-full px-8 md:px-16 text-center">
                    <Quote className="w-12 h-12 text-blue-300 mx-auto mb-6" />

                    <div className="flex justify-center mb-6">
                      {renderStars(testimonials[currentSlide].rating)}
                    </div>

                    <blockquote className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 italic">
                      "{testimonials[currentSlide].content}"
                    </blockquote>

                    <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                      {testimonials[currentSlide].project}
                    </div>

                    <div className="flex items-center justify-center space-x-4">
                      <img
                        src={testimonials[currentSlide].avatar}
                        alt={`Foto profil ${testimonials[currentSlide].name}`}
                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                        loading="lazy"
                      />
                      <div className="text-left">
                        <h4 className="text-lg font-bold text-gray-900">
                          {testimonials[currentSlide].name}
                        </h4>
                        <p className="text-gray-600">
                          {testimonials[currentSlide].position}
                        </p>
                        <p className="text-blue-600 font-medium">
                          {testimonials[currentSlide].company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Slide Indicators */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "bg-blue-600 w-8"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-16 bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">4.9/5</div>
                <div className="text-gray-700 font-medium">Average Rating</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600 mb-2">98%</div>
                <div className="text-gray-700 font-medium">Client Retention</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-700 font-medium">Projects Delivered</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600 mb-2">50+</div>
                <div className="text-gray-700 font-medium">Happy Clients</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
